import React, { useState, useEffect } from "react";
import * as S from "./ReceiptsStyle";
import { receiptService } from "../../Services/receiptService";
import Loading from "../Loader";
import Receipt from "./Receipt/Receipt";
import CreateReceipt from "./CreateReceipt";
import { FaSearch } from "react-icons/fa";
import { useSearchParams } from "react-router-dom"; // <--- 1. Importar useSearchParams

export default function Receipts() {
  // <--- 2. Obter os parâmetros da URL
  const [searchParams] = useSearchParams();
  const initialClientSearch = searchParams.get("clientSearch") || "";

  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(10);

  // Filtros
  // <--- 3. Inicializar o estado com o valor da URL (ou vazio se não tiver)
  const [clientName, setClientName] = useState(initialClientSearch);
  const [contractId, setContractId] = useState("");
  const [withdrawId, setWithdrawId] = useState("");

  // Modais
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // <--- 4. useEffect para detectar mudanças na URL mesmo se o componente já estiver montado
  useEffect(() => {
    const queryClient = searchParams.get("clientSearch");
    if (queryClient && queryClient !== clientName) {
      setClientName(queryClient);
      // Se a URL mudar, queremos resetar a página e buscar novamente
      // Porém, como o setClientName é assíncrono, passamos o valor direto para uma nova função de fetch ou deixamos o fluxo seguir
      // Para simplificar e garantir que funcione na montagem inicial, o useState acima já resolve.
      // Este useEffect garante casos de navegação interna.
      fetchDataOverride(queryClient);
    } else {
      // Fluxo normal (montagem inicial ou mudança de página)
      fetchData();
    }
  }, [page, searchParams]); // Adicionado searchParams

  // Função auxiliar para buscar forçando um nome específico (usado quando a URL muda)
  const fetchDataOverride = async (overrideName) => {
    setLoading(true);
    try {
      const data = await receiptService.searchReceipts(
        overrideName,
        contractId,
        withdrawId,
        1, // Reseta para página 1 ao mudar filtro via URL
        pageSize
      );
      setReceipts(data.items);
      setTotalPages(data.totalPages);
      setPage(1);
    } catch (error) {
      console.error("Erro ao buscar recibos", error);
    }
    setLoading(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await receiptService.searchReceipts(
        clientName, // Usa o estado atual
        contractId,
        withdrawId,
        page,
        pageSize
      );
      setReceipts(data.items);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error("Erro ao buscar recibos", error);
    }
    setLoading(false);
  };

  const handleSearch = () => {
    setPage(1);
    // Aqui chamamos fetchData diretamente, ele pegará os estados atualizados dos inputs
    // Pequeno timeout para garantir que o estado do React atualizou se estivesse digitando rápido
    setTimeout(() => fetchData(), 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return (
      date.toLocaleDateString("pt-BR") + " " + date.toLocaleTimeString("pt-BR")
    );
  };

  return (
    <S.Container>
      <Loading load={loading} />

      {/* Modal de Visualização */}
      {selectedReceipt && (
        <Receipt
          receipt={selectedReceipt}
          onClose={() => setSelectedReceipt(null)}
        />
      )}

      {/* Modal de Criação */}
      {isCreateModalOpen && (
        <CreateReceipt
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            fetchData();
          }}
        />
      )}

      <S.ContentBox>
        <S.Header>
          <h2>Gerenciar Recibos</h2>
          <S.CreateButton onClick={() => setIsCreateModalOpen(true)}>
            + Novo Recibo
          </S.CreateButton>
        </S.Header>

        <S.SearchArea>
          <S.InputGroup>
            <label>Nome do Cliente</label>
            <input
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Digite o nome"
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            />
          </S.InputGroup>
          <S.InputGroup>
            <label>ID Contrato</label>
            <input
              type="number"
              value={contractId}
              onChange={(e) => setContractId(e.target.value)}
              placeholder="ID"
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            />
          </S.InputGroup>
          <S.InputGroup>
            <label>ID Saque</label>
            <input
              type="number"
              value={withdrawId}
              onChange={(e) => setWithdrawId(e.target.value)}
              placeholder="ID"
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            />
          </S.InputGroup>
          <S.SearchButton onClick={handleSearch}>
            <FaSearch /> Buscar
          </S.SearchButton>
        </S.SearchArea>

        <S.TableContainer>
          <S.Table>
            <thead>
              <tr>
                <S.TableHeaderCell>ID</S.TableHeaderCell>
                <S.TableHeaderCell>Data Criação</S.TableHeaderCell>
                <S.TableHeaderCell>Cliente</S.TableHeaderCell>
                <S.TableHeaderCell>Tipo</S.TableHeaderCell>
                <S.TableHeaderCell>Ref. ID</S.TableHeaderCell>
                <S.TableHeaderCell>Descrição</S.TableHeaderCell>
              </tr>
            </thead>
            <tbody>
              {receipts.map((item) => (
                <S.TableRow
                  key={item.id}
                  onClick={() => setSelectedReceipt(item)}
                >
                  <td>{item.id}</td>
                  <td>{formatDate(item.dateCreated)}</td>
                  <td>{item.clientName || "Cliente #" + item.clientId}</td>
                  <td>
                    <S.TypeBadge isWithdraw={item.type === "RECIBO_DE_SAQUE"}>
                      {item.type === "RECIBO_DE_SAQUE" ? "SAQUE" : "CONTRATO"}
                    </S.TypeBadge>
                  </td>
                  <td>
                    {item.type === "RECIBO_DE_SAQUE"
                      ? `Saque #${item.itemId}`
                      : `Contrato #${item.contractId}`}
                  </td>
                  <td>{item.description}</td>
                </S.TableRow>
              ))}
            </tbody>
          </S.Table>
        </S.TableContainer>

        <S.PaginationContainer>
          <S.PaginationButton
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Anterior
          </S.PaginationButton>
          <span>
            Página {page} de {totalPages || 1}
          </span>
          <S.PaginationButton
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            Próxima
          </S.PaginationButton>
        </S.PaginationContainer>
      </S.ContentBox>
    </S.Container>
  );
}
